import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  ArrowPathIcon,
  ArrowUpRightIcon,
  ArrowDownLeftIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  dismissAnfrageMailCandidate,
  getAnfrageMailCandidates,
  getAnfrageMailThread,
  type AnfrageMailCandidate,
  type AnfrageMailThreadItem,
} from "@/lib/anfragen";
import { fmtMailDate, matchReasonLabel, plainTextToSafeMailHtml } from "@/pages/anfragen/anfrageWizardUtils";

export interface MailContentRef {
  ref_type: string;
  ref_id?: number | string | null;
}

function isOutgoing(item: AnfrageMailThreadItem): boolean {
  return item.kind === "sent";
}

function threadRole(item: AnfrageMailThreadItem): string {
  if (isOutgoing(item)) return "Anfrage gesendet";
  if (item.branch === "archive") return "Antwort abgelegt";
  return "Antwort";
}

function threadAddrLine(item: AnfrageMailThreadItem): string {
  const party = item.party || "–";
  if (isOutgoing(item)) return `an ${party}`;
  return `von ${party}`;
}

function CandidateCard({
  candidate,
  onOpen,
  onConfirm,
  onDismiss,
  dismissing,
}: {
  candidate: AnfrageMailCandidate;
  onOpen: () => void;
  onConfirm: () => void;
  onDismiss: () => void;
  dismissing: boolean;
}) {
  const previewHtml = plainTextToSafeMailHtml((candidate.snippet || "").slice(0, 260));
  const attN = candidate.attachments_summary?.length || 0;
  const who = candidate.from_name || candidate.from_email || "–";
  const reasons = (candidate.match_reasons || []).map(matchReasonLabel);

  return (
    <li className="rounded-ios border border-status-green/40 bg-card p-3 shadow-ios">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-status-green px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
              Neue Antwort
            </span>
            <span className="text-xs text-label-tertiary">{fmtMailDate(candidate.received_at)}</span>
          </div>
          <p className="mt-1 truncate text-sm font-semibold text-label">{candidate.subject || "(ohne Betreff)"}</p>
          <p className="truncate text-xs text-label-secondary">
            von {who}
            {candidate.from_name && candidate.from_email ? ` <${candidate.from_email}>` : ""}
          </p>
        </div>
        {candidate.has_attachments || attN > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-fill-tertiary px-2 py-0.5 text-xs text-label-secondary">
            <PaperClipIcon className="h-3.5 w-3.5" aria-hidden />
            {attN || "≥1"} {attN === 1 ? "Anhang" : "Anhänge"}
          </span>
        ) : null}
      </div>

      <div
        className="mt-2 line-clamp-3 text-sm text-label-secondary [&_p]:m-0"
        dangerouslySetInnerHTML={{ __html: previewHtml }}
      />

      {reasons.length || (candidate.matched_ta_nummern || []).length ? (
        <p className="mt-2 text-[11px] text-label-tertiary">
          Zuordnung: {reasons.join(", ")}
          {(candidate.matched_ta_nummern || []).length
            ? ` · TA ${(candidate.matched_ta_nummern || []).join(", ")}`
            : ""}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onOpen}>
          Mail lesen
        </Button>
        <Button type="button" size="sm" onClick={onConfirm}>
          Ablegen &amp; zuordnen
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={dismissing} loading={dismissing} onClick={onDismiss}>
          Gehört nicht dazu
        </Button>
      </div>
    </li>
  );
}

function ThreadItem({
  item,
  onOpen,
}: {
  item: AnfrageMailThreadItem;
  onOpen: () => void;
}) {
  const outgoing = isOutgoing(item);
  const previewHtml = plainTextToSafeMailHtml((item.preview || "").slice(0, 240));
  const clickable = !!item.detail?.ref_type;

  return (
    <li className="relative ps-8">
      <span
        className={clsx(
          "absolute start-0 top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-card",
          outgoing ? "bg-accent-soft text-accent" : "bg-status-green/15 text-status-green",
        )}
        aria-hidden
      >
        {outgoing ? (
          <ArrowUpRightIcon className="h-3.5 w-3.5" />
        ) : (
          <ArrowDownLeftIcon className="h-3.5 w-3.5" />
        )}
      </span>
      <button
        type="button"
        disabled={!clickable}
        onClick={onOpen}
        className={clsx(
          "w-full rounded-ios border border-separator bg-card p-3 text-start transition",
          clickable ? "hover:border-accent/50 hover:shadow-ios" : "cursor-default",
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <span className="text-xs font-semibold text-label-secondary">
            {threadRole(item)} <span className="font-normal text-label-tertiary">{threadAddrLine(item)}</span>
          </span>
          <span className="text-xs text-label-tertiary">{item.sort_at ? fmtMailDate(item.sort_at) : ""}</span>
        </div>
        <p className="mt-1 truncate text-sm font-semibold text-label">{item.subject || "(ohne Betreff)"}</p>
        <div
          className="mt-0.5 line-clamp-2 text-xs text-label-secondary [&_p]:m-0"
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
        {item.has_attachments ? (
          <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-fill-tertiary px-2 py-0.5 text-xs text-label-secondary">
            <PaperClipIcon className="h-3.5 w-3.5" aria-hidden /> Anhang
          </span>
        ) : null}
      </button>
    </li>
  );
}

export function MailThreadSection({
  anfrageId,
  onOpenMailRead,
  onConfirmCandidate,
  onDismissCandidate,
  onRefresh,
}: {
  anfrageId: number | null;
  onOpenMailRead: (ref: MailContentRef) => void;
  onConfirmCandidate: (candidateId: number) => void;
  onDismissCandidate: (candidateId: number) => void;
  onRefresh?: () => void;
}) {
  const [thread, setThread] = useState<AnfrageMailThreadItem[]>([]);
  const [candidates, setCandidates] = useState<AnfrageMailCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [dismissing, setDismissing] = useState<number | null>(null);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const load = useCallback(async () => {
    if (!anfrageId) {
      setThread([]);
      setCandidates([]);
      return;
    }
    setLoading(true);
    try {
      const [data, fallbackThread] = await Promise.all([
        getAnfrageMailCandidates(anfrageId),
        getAnfrageMailThread(anfrageId),
      ]);
      const pending = (data.candidates || []).filter((c) => c.status === "pending");
      setCandidates(pending);
      let threadItems = data.thread || [];
      if (!threadItems.length) threadItems = fallbackThread;
      // Neueste zuerst
      setThread(
        [...threadItems].sort((a, b) => {
          const ta = a.sort_at ? new Date(a.sort_at).getTime() : 0;
          const tb = b.sort_at ? new Date(b.sort_at).getTime() : 0;
          return tb - ta;
        }),
      );
    } catch {
      setThread([]);
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [anfrageId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDismiss(candidateId: number) {
    if (!anfrageId) return;
    if (
      !window.confirm(
        "Diese Mail gehört nicht zu der Anfrage? Sie bleibt im Postfach, wird hier aber nicht mehr vorgeschlagen.",
      )
    )
      return;
    setDismissing(candidateId);
    try {
      await dismissAnfrageMailCandidate(anfrageId, candidateId);
      onDismissCandidate(candidateId);
      await load();
    } catch {
      /* parent may show feedback */
    } finally {
      setDismissing(null);
    }
  }

  if (!anfrageId) return null;

  const pendingCount = candidates.length;

  return (
    <section className="rounded-ios border border-separator bg-card p-3" aria-label="Antworten und Mail-Verlauf">
      <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-label">
          Antworten &amp; Mail-Verlauf
          {pendingCount > 0 ? (
            <span className="rounded-full bg-status-green px-2 py-0.5 text-xs font-bold text-white">
              {pendingCount} neu
            </span>
          ) : null}
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          loading={loading}
          onClick={() => void load()}
          title="Postfach erneut prüfen"
          leftIcon={<ArrowPathIcon className="h-4 w-4" aria-hidden />}
        >
          Aktualisieren
        </Button>
      </header>

      {loading && !thread.length && !candidates.length ? (
        <p className="flex items-center gap-2 py-2 text-sm text-label-secondary">
          <Spinner /> Lade Mails …
        </p>
      ) : null}

      {candidates.length > 0 ? (
        <div className="mb-4">
          <p className="mb-2 text-xs text-label-secondary">
            Diese Mails aus dem Postfach passen wahrscheinlich zu dieser Anfrage. „Ablegen“ speichert Mail und
            Anhänge im Projektordner und verknüpft sie hier.
          </p>
          <ul className="space-y-2" role="list" aria-label="Neue Antworten">
            {candidates.map((c) => (
              <CandidateCard
                key={c.id}
                candidate={c}
                dismissing={dismissing === c.id}
                onOpen={() => onOpenMailRead({ ref_type: "candidate", ref_id: c.id })}
                onConfirm={() => onConfirmCandidate(c.id)}
                onDismiss={() => void handleDismiss(c.id)}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {thread.length > 0 ? (
        <ol className="relative space-y-2 border-s border-separator ms-3 ps-0 [&>li]:-ms-3" role="list">
          {thread.map((it, idx) => {
            const ref = it.detail;
            const key = `${ref?.ref_type || "item"}-${ref?.ref_id ?? idx}`;
            return (
              <ThreadItem
                key={key}
                item={it}
                onOpen={() => {
                  if (ref?.ref_type) onOpenMailRead({ ref_type: ref.ref_type, ref_id: ref.ref_id });
                }}
              />
            );
          })}
        </ol>
      ) : !loading && !candidates.length ? (
        <p className="rounded-ios border border-dashed border-separator-opaque p-4 text-center text-sm text-label-secondary">
          Noch keine Mails zu dieser Anfrage. Sobald der Lieferant antwortet, erscheint die Mail hier automatisch.
        </p>
      ) : null}
    </section>
  );
}
